import { useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  TextInput,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { calculateE1RM as calculateCanonicalE1RM, calculateDeadHangScore } from '@/lib/lifts/canonical';
import { ChevronLeft, Swords } from 'lucide-react-native';
import { useTheme } from '@/contexts/ThemeContext';
import { TYPO } from '@/constants/typography';
import OnboardingProgress from '@/components/OnboardingProgress';
import { clearBaselineCache } from '@/lib/baselineClient';

interface LiftData {
  weight: string;
  reps: string;
}

interface LiftEntry {
  canonical_name: string;
  display_name: string;
  db_canonical?: string;   // Value for canonical_name column in DB
  placeholder_weight: string;
  placeholder_reps: string;
  helper_text: string;
  is_time_based?: boolean;
}

const LIFTS: LiftEntry[] = [
  {
    canonical_name: 'bench-press',
    display_name: 'Bench Press',
    db_canonical: 'bench-press',
    placeholder_weight: '135',
    placeholder_reps: '8',
    helper_text: 'Your best recent set',
  },
  {
    canonical_name: 'back-squat',
    display_name: 'Back Squat',
    db_canonical: 'back-squat',
    placeholder_weight: '185',
    placeholder_reps: '5',
    helper_text: 'Your best recent set',
  },
  {
    canonical_name: 'deadlift',
    display_name: 'Deadlift',
    db_canonical: 'deadlift',
    placeholder_weight: '225',
    placeholder_reps: '5',
    helper_text: 'Your best recent set',
  },
  {
    canonical_name: 'weighted-pull-up',
    display_name: 'Weighted Pull-Up',
    db_canonical: 'weighted-pull-up',
    placeholder_weight: '25',
    placeholder_reps: '5',
    helper_text: 'Added weight only (bodyweight added automatically)',
  },
  {
    canonical_name: 'dead-hang',
    display_name: 'Dead Hang',
    db_canonical: 'dead-hang',
    placeholder_weight: '0',
    placeholder_reps: '60',
    helper_text: 'Just hang from a bar — enter seconds held',
    is_time_based: true,
  },
];

export default function OnboardingBaseline() {
  const router = useRouter();
  const { user, profile, refreshProfile } = useAuth();
  const { theme } = useTheme();
  const c = theme.colors;
  const insets = useSafeAreaInsets();

  const [lifts, setLifts] = useState<Record<string, LiftData>>({
    'bench-press': { weight: '', reps: '' },
    'back-squat': { weight: '', reps: '' },
    'deadlift': { weight: '', reps: '' },
    'weighted-pull-up': { weight: '', reps: '' },
    'dead-hang': { weight: '', reps: '' },
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [skippedLifts, setSkippedLifts] = useState<Record<string, boolean>>({});

  // Use canonical Brzycki formula for consistency with scoring pipeline
  const calculateE1RM = (weight: number, reps: number, isWeightedPullUp = false): number => {
    let totalWeight = weight;
    
    // For weighted pull-ups, add bodyweight for display purposes
    if (isWeightedPullUp && profile?.bodyweight) {
      totalWeight = weight + profile.bodyweight;
    }
    
    return calculateCanonicalE1RM(totalWeight, reps);
  };

  const updateLift = (canonical_name: string, field: 'weight' | 'reps', value: string) => {
    // Only allow numbers and decimal point
    const cleaned = value.replace(/[^0-9.]/g, '');
    setLifts(prev => ({
      ...prev,
      [canonical_name]: {
        ...prev[canonical_name],
        [field]: cleaned,
      },
    }));
    setError('');
  };

  const getE1RM = (canonical_name: string): number => {
    const lift = lifts[canonical_name];
    const weight = parseFloat(lift.weight) || 0;
    const reps = parseFloat(lift.reps) || 0;
    
    // Dead hang: use canonical scoring (bodyweight × seconds)
    if (canonical_name === 'dead-hang') {
      const bw = profile?.bodyweight ?? 175;
      return calculateDeadHangScore(reps, bw);
    }
    
    if (weight <= 0 || reps <= 0) return 0;
    
    return calculateE1RM(weight, reps, canonical_name === 'weighted-pull-up');
  };

  const handleContinue = async () => {
    // Validate that we have at least one lift with valid data
    const validLifts = LIFTS.filter(lift => {
      // Skip lifts the user explicitly skipped
      if (skippedLifts[lift.canonical_name]) return false;
      const data = lifts[lift.canonical_name];
      const weight = parseFloat(data.weight) || 0;
      const reps = parseFloat(data.reps) || 0;
      // Dead hang only needs seconds > 0
      if (lift.is_time_based) return reps > 0;
      // Pull-ups: allow weight=0 (bodyweight only), just need reps > 0
      if (lift.canonical_name === 'weighted-pull-up') return reps > 0;
      return weight > 0 && reps > 0;
    });

    if (validLifts.length === 0) {
      setError('Please enter at least one lift with valid weight and reps');
      return;
    }

    setSaving(true);
    setError('');

    try {
      if (user?.id) {
        // Prepare strength events data (matches actual DB schema)
        const userUnit = profile?.units || 'lbs';
        const strengthEvents = validLifts.map(lift => {
          const data = lifts[lift.canonical_name];
          let weight = parseFloat(data.weight) || 0;
          const reps = parseFloat(data.reps) || 0;

          // Don't replace weight=0 with bodyweight for weighted pull-ups;
          // the scoring pipeline handles bodyweight addition.

          return {
            profile_id: user.id,
            canonical_name: lift.db_canonical || lift.display_name,
            lift_key: lift.db_canonical || lift.canonical_name,
            weight: weight || 1, // Ensure weight > 0 for DB constraints
            weight_unit: userUnit === 'kg' ? 'kg' : 'lb',
            reps: Math.round(reps),
            source: 'workout',
            performed_at: new Date().toISOString(),
          };
        });

        // Save all lifts
        const { error: insertError } = await supabase
          .from('strength_events')
          .insert(strengthEvents);

        if (insertError) {
          console.error('Failed to save baseline lifts:', JSON.stringify(insertError));
          setError(`Save failed: ${insertError.message || insertError.code || 'Unknown error'}`);
          setSaving(false);
          return;
        }

        clearBaselineCache(user?.id);
        await refreshProfile?.();
      }

      // Navigate to score reveal
      router.replace('/(onboarding)/score-reveal');
    } catch (err) {
      console.error('Baseline save error:', err);
      setError('Something went wrong. Please try again.');
    }

    setSaving(false);
  };

  return (
    <View style={[styles.container, { backgroundColor: c.background }]}>
      <TouchableOpacity style={[styles.backBtn, { top: insets.top + 8 }]} onPress={() => router.back()}>
        <ChevronLeft size={24} color={c.text} />
      </TouchableOpacity>

      <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : 'height'} style={{ flex: 1 }}>
      <ScrollView contentContainerStyle={styles.scrollContent} keyboardShouldPersistTaps="handled">
        <View style={styles.inner}>
          <OnboardingProgress step={5} />
          <Swords size={36} color={c.accent} />
          <Text style={[styles.title, { color: c.text, fontFamily: TYPO.heading.bold }]}>
            ENTER YOUR CORE 5
          </Text>
          <Text style={[styles.subtitle, { color: c.textSecondary }]}>
            Your best recent numbers for the 5 lifts that power your Strength Score.
          </Text>

          <View style={styles.liftsContainer}>
            {LIFTS.map((lift) => {
              const liftData = lifts[lift.canonical_name];
              const e1rm = getE1RM(lift.canonical_name);
              const isSkipped = skippedLifts[lift.canonical_name] ?? false;
              
              return (
                <View key={lift.canonical_name} style={[styles.liftCard, { backgroundColor: c.surface, borderColor: c.border, opacity: isSkipped ? 0.45 : 1 }]}>
                  <View style={styles.liftCardHeader}>
                    <Text style={[styles.liftName, { color: c.text, fontFamily: TYPO.body.semibold }]}>
                      {lift.display_name}
                    </Text>
                    <TouchableOpacity
                      onPress={() => setSkippedLifts(prev => ({ ...prev, [lift.canonical_name]: !prev[lift.canonical_name] }))}
                      hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                    >
                      <Text style={[styles.skipBtn, { color: isSkipped ? c.accent : c.textTertiary }]}>
                        {isSkipped ? 'Undo' : 'Skip'}
                      </Text>
                    </TouchableOpacity>
                  </View>

                  {isSkipped ? (
                    <Text style={[styles.skipMessage, { color: c.textSecondary }]}>
                      No worries! You can add this later.
                    </Text>
                  ) : (
                    <>
                      <View style={styles.inputRow}>
                        {!lift.is_time_based && (
                          <View style={styles.inputGroup}>
                            <Text style={[styles.inputLabel, { color: c.textSecondary }]}>
                              WEIGHT
                            </Text>
                            <TextInput
                              style={[styles.input, { color: c.text, backgroundColor: c.background, borderColor: c.border }]}
                              value={liftData.weight}
                              onChangeText={(text) => updateLift(lift.canonical_name, 'weight', text)}
                              placeholder={lift.placeholder_weight}
                              placeholderTextColor={c.textTertiary}
                              keyboardType="numeric"
                              returnKeyType="next"
                            />
                          </View>
                        )}
                        
                        <View style={lift.is_time_based ? styles.inputGroupFull : styles.inputGroup}>
                          <Text style={[styles.inputLabel, { color: c.textSecondary }]}>
                            {lift.is_time_based ? 'SECONDS' : 'REPS'}
                          </Text>
                          <TextInput
                            style={[styles.input, { color: c.text, backgroundColor: c.background, borderColor: c.border }]}
                            value={liftData.reps}
                            onChangeText={(text) => updateLift(lift.canonical_name, 'reps', text)}
                            placeholder={lift.placeholder_reps}
                            placeholderTextColor={c.textTertiary}
                            keyboardType="numeric"
                            returnKeyType="next"
                          />
                        </View>
                      </View>

                      <Text style={[styles.helperText, { color: c.textTertiary }]}>
                        {lift.helper_text}
                      </Text>

                      {e1rm > 0 && !lift.is_time_based && (
                        <Text style={[styles.e1rmText, { color: c.accent }]}>
                          Est. 1RM: {Math.round(e1rm)} {profile?.units || 'lbs'}
                        </Text>
                      )}
                    </>
                  )}
                </View>
              );
            })}
          </View>

          {error ? (
            <Text style={[styles.errorText, { color: c.error || '#ff4444' }]}>{error}</Text>
          ) : null}
        </View>

        <View style={styles.bottom}>
          <TouchableOpacity
            style={[
              styles.primaryBtn,
              {
                backgroundColor: c.accent,
                opacity: saving ? 0.6 : 1,
              },
            ]}
            onPress={handleContinue}
            disabled={saving}
          >
            {saving ? (
              <ActivityIndicator size="small" color={c.accentForeground} />
            ) : (
              <Text style={[styles.primaryBtnText, { color: c.accentForeground, fontFamily: TYPO.heading.bold }]}>
                CONTINUE
              </Text>
            )}
          </TouchableOpacity>
        </View>
      </ScrollView>
      </KeyboardAvoidingView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  backBtn: { position: 'absolute', top: 50, left: 16, zIndex: 10, padding: 8 },
  scrollContent: { flexGrow: 1, paddingBottom: 120 },
  inner: { flex: 1, paddingHorizontal: 24, paddingTop: 80, alignItems: 'center', gap: 16 },
  title: { fontSize: 28, textAlign: 'center', textTransform: 'uppercase', letterSpacing: 1 },
  subtitle: { fontSize: 15, textAlign: 'center', lineHeight: 22, maxWidth: 340, marginBottom: 8 },
  liftsContainer: { width: '100%', gap: 16 },
  liftCard: {
    borderWidth: 1,
    borderRadius: 2,
    padding: 16,
    gap: 12,
  },
  liftCardHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  liftName: { fontSize: 18, textAlign: 'center', flex: 1 },
  skipBtn: { fontSize: 13, fontWeight: '600', paddingHorizontal: 8 },
  skipMessage: { fontSize: 13, textAlign: 'center', fontStyle: 'italic', paddingVertical: 8 },
  inputRow: { flexDirection: 'row', gap: 12 },
  inputGroup: { flex: 1, gap: 4 },
  inputGroupFull: { flex: 1, gap: 4 },
  inputLabel: { fontSize: 10, fontWeight: '700', letterSpacing: 1, textTransform: 'uppercase' },
  input: {
    borderWidth: 1,
    borderRadius: 2,
    paddingHorizontal: 12,
    height: 44,
    fontSize: 16,
    fontWeight: '700',
    textAlign: 'center',
  },
  helperText: { fontSize: 11, textAlign: 'center', fontStyle: 'italic' },
  e1rmText: { fontSize: 13, textAlign: 'center', fontWeight: '700' },
  errorText: { fontSize: 13, textAlign: 'center' },
  bottom: { paddingHorizontal: 24, paddingBottom: 40, alignItems: 'center' },
  primaryBtn: { width: '100%', paddingVertical: 18, borderRadius: 0, alignItems: 'center' },
  primaryBtnText: { fontSize: 18, textTransform: 'uppercase', letterSpacing: 1 },
});
